import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "convex/react";
import { api } from "@todotogether/backend/_generated/api";
import {
  WEEKDAY_HEADER,
  buildDayPanel,
  densityByDay,
  dueInfo,
  lastKnownOverdue,
  matrixWindow,
  monthMatrix,
  monthOf,
  monthTitle,
  shadeForCounts,
  todayHasOverdue,
  utcDayStart,
  walkMonth,
  type CalendarDay,
  type DensityStep,
  type Month,
  type PanelItem,
} from "@todotogether/shared";

/**
 * The web calendar (ticket #216): the mobile pair's month grid (#213)
 * and day panel (#214) on the signed-in web shell. The day math is the
 * PAIR'S OWN implementation — the pure modules hoisted verbatim to
 * @todotogether/shared (#216) — so there is no second, divergent
 * derivation; this page is the web spelling of the same semantics.
 * Item-bearing days FILL with the density ramp (#268), today is a ring
 * around whatever fill it has (bare when nothing is due), and the
 * selected day thickens the ring; adjacent-month cells are dimmed but
 * live (a tap walks the view to that day's month). Overdue items fill
 * their true dates — nothing rolls forward to today.
 *
 * The day panel is the grilled spec's WEB composition: it sits BESIDE
 * the grid on wide viewports (the md two-column spell) and stacks
 * BELOW on narrow ones (DOM order, not positioning). Today's panel is
 * the special one: the cross-list Overdue group (items:overdueWindow,
 * server-capped at 50 with a "+N more" line) above the due-today list
 * groups; any other day shows only that day's rows — the overdue group
 * is structurally today-only inside buildDayPanel. Rows are read-only
 * (no tick, no edit affordances — editing lives in the list screen);
 * each group's list name links to the web list screen.
 *
 * View state lives in component state ON PURPOSE: web navigation is
 * route-based (no mobile stack keeping this screen mounted), so a trip
 * to a list and back remounts at the current month — the #214
 * stack-snapshot round trip has no web route counterpart and nothing
 * in the ticket asks for one.
 *
 * Today is a RING around whatever fill it has — the fill channel
 * belongs to density alone, so the busiest day can never be mistaken
 * for today; today still counts as bearing items for taps when
 * overdue items exist (the ring follows due-today OR overdue, through
 * the same last-known settle seam as mobile, #272).
 */
export default function CalendarPage() {
  const [month, setMonth] = useState<Month>(() => monthOf(Date.now()));
  const [selectedDayMs, setSelectedDayMs] = useState<number | null>(null);

  const matrix = useMemo(() => monthMatrix(month), [month]);
  const window = useMemo(() => matrixWindow(matrix.weeks), [matrix]);
  const rows = useQuery(api.items.monthWindow, window);
  const todayMs = utcDayStart(Date.now());
  // The before-today half of today's panel — and the overdue half of
  // the today marker. The live response settles through the viewer's
  // last-known one, so the today marker never regresses to "no" while
  // the query is in flight on a remount (#272).
  const me = useQuery(api.users.me);
  const liveOverdue = useQuery(api.items.overdueWindow, { beforeMs: todayMs });
  const overdue = lastKnownOverdue(me?._id, liveOverdue);
  // Density (#268): day → due-item count, and the per-month shade map
  // the counts normalize into. Rows arrive incomplete-only (done is
  // filtered inside the query).
  const density = useMemo(
    () => densityByDay(rows ?? []),
    [rows],
  );
  const shades = useMemo(() => shadeForCounts(density.values()), [density]);
  const todayBearing =
    (rows !== undefined && density.has(todayMs)) || todayHasOverdue(overdue);

  const panel = useMemo(() => {
    if (selectedDayMs === null || rows === undefined || rows === null) {
      return null;
    }
    return buildDayPanel({
      selectedDayMs,
      todayMs,
      rows,
      overdue,
    });
  }, [selectedDayMs, rows, overdue, todayMs]);

  const onDayTap = (day: CalendarDay) => {
    const dayMonth = monthOf(day.dayMs);
    const adjacent =
      dayMonth.year !== month.year || dayMonth.month !== month.month;
    // Adjacent cells keep #213's month-walk. Selection follows the
    // fill: an item-bearing day opens (or toggles off) its panel, a
    // bare day tap closes it.
    if (adjacent) setMonth(dayMonth);
    const bearing =
      day.dayMs === todayMs ? todayBearing : density.has(day.dayMs);
    if (!bearing) {
      setSelectedDayMs(null);
      return;
    }
    setSelectedDayMs((current) =>
      current === day.dayMs ? null : day.dayMs,
    );
  };

  // Month navigation closes the panel (the mobile pair's rule): the
  // selected day leaves the grid with its month, and a panel for an
  // off-screen day would read "Nothing due" from the new window's rows
  // — a lie. The next tap re-opens whatever the new month fills.
  const goPrev = () => {
    setMonth(walkMonth(month, -1));
    setSelectedDayMs(null);
  };
  const goNext = () => {
    setMonth(walkMonth(month, 1));
    setSelectedDayMs(null);
  };
  const goToday = () => {
    setMonth(monthOf(Date.now()));
    setSelectedDayMs(null);
  };

  return (
    <main className="min-h-screen bg-paper px-6 py-10">
      <div className="mx-auto max-w-4xl">
        <Link
          to="/app"
          className="text-sm text-muted hover:underline"
          data-testid="back-to-lists"
        >
          ← Your lists
        </Link>
        <header className="mt-4 flex items-center justify-between">
          <h1
            className="font-serif text-[28px] font-bold leading-[34px] text-ink"
            data-testid="calendar-heading"
          >
            Calendar
          </h1>
        </header>

        {/* The month bar stays above the two-column area: title,
            unbounded prev/next walking, Today. */}
        <div className="mt-4 flex items-center gap-1.5">
          <button
            type="button"
            onClick={goPrev}
            data-testid="calendar-prev-month"
            aria-label="Previous month"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-2xl text-blue hover:bg-pressed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue"
          >
            ‹
          </button>
          <h2
            data-testid="calendar-title"
            className="min-w-0 flex-1 truncate text-center font-serif text-lg font-bold text-ink sm:text-2xl"
          >
            {monthTitle(month)}
          </h2>
          <button
            type="button"
            onClick={goNext}
            data-testid="calendar-next-month"
            aria-label="Next month"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-2xl text-blue hover:bg-pressed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue"
          >
            ›
          </button>
          <button
            type="button"
            onClick={goToday}
            data-testid="calendar-today"
            className="ml-1 flex h-11 shrink-0 items-center rounded-lg bg-wash px-3 text-sm font-semibold text-blue hover:bg-wash-pressed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue"
          >
            Today
          </button>
        </div>

        {/* Wide viewports: the day panel sits BESIDE the grid (the
            grilled spec's web composition). Narrow: this is plain flow,
            so the panel stacks below — order, not positioning. */}
        <div className="mt-4 gap-4 md:grid md:grid-cols-2 md:items-start">
          <section
            data-testid="calendar-grid"
            className="rounded-xl border border-line bg-page p-3 shadow-sm sm:p-4"
          >
            <div className="grid grid-cols-7 pb-1" data-testid="calendar-weekdays">
              {WEEKDAY_HEADER.map((label, i) => (
                <span
                  key={`${label}-${i}`}
                  className="text-center text-xs font-semibold text-faint"
                >
                  {label}
                </span>
              ))}
            </div>
            {matrix.weeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7">
                {week.map((day) => {
                  const count = density.get(day.dayMs);
                  return (
                    <DayCell
                      key={day.iso}
                      day={day}
                      count={count}
                      step={count !== undefined ? shades.get(count) : undefined}
                      isToday={day.dayMs === todayMs}
                      todayBearing={todayBearing}
                      selected={day.dayMs === selectedDayMs}
                      onDayTap={onDayTap}
                    />
                  );
                })}
              </div>
            ))}
          </section>

          {panel !== null && (
            <section
              data-testid="calendar-panel"
              className="mt-4 rounded-xl border border-line bg-page p-4 shadow-sm md:mt-0"
            >
              {/* Today's special composition: the cross-list Overdue
                  group above the due-today list groups. Panel rows are
                  data only — no tick, no edit affordances (#214);
                  editing lives in the list screen. */}
              {/* The heading only earns its rows: a today with nothing
                  overdue renders no Overdue section at all. */}
              {todayHasOverdue(panel.overdue) && (
                <div data-testid="calendar-panel-overdue">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-muted">
                    Overdue
                  </h3>
                  {panel.overdue.items.map((item) => (
                    <PanelRow key={item.itemId} item={item} showDate />
                  ))}
                  {panel.overdue.overflowCount > 0 && (
                    <p
                      className="pt-2 text-sm font-semibold text-faint"
                      data-testid="calendar-overdue-more"
                    >
                      +{panel.overdue.overflowCount} more
                    </p>
                  )}
                </div>
              )}
              {panel.groups.map((group) => (
                <div key={group.listId} className="mt-4">
                  {/* The group's list name is the tap target: it links
                      to the web list screen (route-based navigation —
                      the calendar remounts at the current month on
                      return; the mobile stack round trip has no web
                      counterpart). */}
                  <Link
                    to={`/app/list/${group.listId}`}
                    data-testid={`calendar-panel-list-${group.listId}`}
                    className="text-sm font-semibold text-blue hover:underline"
                  >
                    {group.listName}
                  </Link>
                  <div className="mt-1">
                    {group.items.map((item) => (
                      <PanelRow key={item.itemId} item={item} />
                    ))}
                  </div>
                </div>
              ))}
              {panel.groups.length === 0 && !todayHasOverdue(panel.overdue) && (
                <p className="py-2 text-sm text-muted">
                  Nothing due on this day
                </p>
              )}
            </section>
          )}
        </div>
      </div>
    </main>
  );
}

/**
 * The ramp's web spelling, one step per Tailwind token: the classes
 * must be literal for Tailwind to generate them (a dynamic
 * `bg-density-${step}` never reaches the CSS). The same tokens exist
 * on mobile (tokens.ts density1–5); step 5's day number pairs with
 * on-blue, steps 1–4 with ink — the contrast-pinned pairing.
 */
const DENSITY_FILL: Record<DensityStep, string> = {
  1: "bg-density-1",
  2: "bg-density-2",
  3: "bg-density-3",
  4: "bg-density-4",
  5: "bg-density-5",
};

function DayCell({
  day,
  count,
  step,
  isToday,
  todayBearing,
  selected,
  onDayTap,
}: {
  day: CalendarDay;
  count: number | undefined;
  step: DensityStep | undefined;
  isToday: boolean;
  todayBearing: boolean;
  selected: boolean;
  onDayTap: (day: CalendarDay) => void;
}) {
  const bearing = isToday ? todayBearing : count !== undefined;
  // The fill channel belongs to density alone (#268) — no ring on
  // item-bearing days. Today's ring sits around whatever fill it has
  // (bare when nothing is due), the picker grid's today treatment; the
  // selected day thickens it. Width and colour are one exclusive
  // spelling per state — Tailwind utilities don't win by class order.
  const circle = selected
    ? "border-[3px] border-blue"
    : isToday
      ? "border-[1.5px] border-blue"
      : "border-[1.5px] border-transparent";
  const fill = step !== undefined ? DENSITY_FILL[step] : undefined;
  return (
    <button
      type="button"
      onClick={() => onDayTap(day)}
      data-testid={`calendar-day-${day.iso}`}
      aria-label={`${monthTitle(monthOf(day.dayMs))} ${day.dayNumber}${
        count !== undefined ? `, ${count} ${count === 1 ? "item" : "items"} due` : ""
      }${selected ? ", selected" : ""}`}
      className="flex aspect-square items-center justify-center"
    >
      <span
        data-testid={
          bearing ? `calendar-dense-${day.iso}` : `calendar-empty-${day.iso}`
        }
        data-today={isToday ? "true" : undefined}
        data-selected={selected ? "true" : undefined}
        className={`flex h-[80%] w-[80%] items-center justify-center rounded-full text-sm ${
          fill ?? ""
        } ${step === 5 ? "text-on-blue" : "text-ink"} ${circle} ${
          day.inMonth ? "" : "opacity-35"
        }`}
      >
        {day.dayNumber}
      </span>
    </button>
  );
}

/**
 * One read-only panel row: the item's title, plus its true due date in
 * the Overdue group — where the date is the row's whole point (the
 * group spans days). Rows inside a day group all share the panel's
 * day, so a label would only repeat the header — and colour every row
 * of a past-day panel in the danger tone for nothing.
 */
function PanelRow({
  item,
  showDate = false,
}: {
  item: PanelItem;
  showDate?: boolean;
}) {
  const due = dueInfo(item.dueAt, utcDayStart(Date.now()));
  return (
    <div
      className="flex items-baseline justify-between gap-2 py-1.5"
      data-testid={`calendar-panel-item-${item.itemId}`}
    >
      <span className="min-w-0 flex-1 truncate text-sm text-ink">
        {item.title}
      </span>
      {showDate && due.tone === "overdue" && (
        <span className="shrink-0 text-sm font-semibold text-danger">
          {due.label}
        </span>
      )}
    </div>
  );
}
